import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Save, Shield, Users, ArrowRight, Languages } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", { name, email });
      toast.success(t("settings.profileSaved"));
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-12">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{t("dashboard.module")}</p>
        <h1 className="font-display text-4xl font-light tracking-tight">{t("settings.title")}</h1>
        <p className="mt-2 text-sm text-zinc-500">{t("settings.description")}</p>
      </div>

      {/* Profile */}
      <section className="em-card p-8">
        <h2 className="font-display text-xl mb-6">{t("settings.profile")}</h2>
        <form onSubmit={saveProfile} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("settings.yourName")}</label>
            <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="em-input" required />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("settings.yourEmail")}</label>
            <input data-testid="settings-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="em-input" required />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">{t("common.role")}</label>
            <div className="em-input flex items-center gap-2 opacity-70 select-none">
              <Shield className="w-4 h-4 text-gold" />
              <span className="uppercase tracking-widest text-xs">{user?.role}</span>
            </div>
          </div>
          <button data-testid="settings-save" type="submit" disabled={saving} className="bg-gold text-ink-0 font-semibold rounded-lg px-6 py-2.5 flex items-center gap-2 hover:bg-gold-hover disabled:opacity-50" style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}>
            <Save className="w-4 h-4" /> {saving ? t("common.loading") : t("settings.saveChanges")}
          </button>
        </form>
      </section>

      {/* Language */}
      <section className="em-card p-8">
        <div className="flex items-center justify-between gap-6">
          <div className="min-w-0">
            <h2 className="font-display text-xl mb-2 flex items-center gap-2">
              <Languages className="w-5 h-5 text-gold" strokeWidth={1.75} />
              {t("settings.language")}
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed">{t("settings.languageDesc")}</p>
          </div>
          <div className="shrink-0">
            <LanguageSwitcher />
          </div>
        </div>
      </section>

      {/* Admin: user management shortcut */}
      {user?.role === "admin" && (
        <Link
          to="/admin/users"
          className="em-card em-card-hover p-8 flex items-center gap-6 group"
        >
          <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-gold" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">{t("common.admin")}</p>
            <h3 className="font-display text-xl mt-1">{t("adminUsers.title")}</h3>
            <p className="text-sm text-zinc-500 mt-1">{t("adminUsers.description")}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-gold group-hover:translate-x-1" style={{ transitionProperty: "color, transform", transitionDuration: "180ms" }} />
        </Link>
      )}
    </div>
  );
}
